<template>
  <!--
    final-version doc 2026-10-05 (problem 5): the old <v-tabs show-arrows>
    strip could not scroll in Arabic. Vuetify 1.5 VTabs scrolls with a
    left-to-right translateX and has no RTL code path, so once the bar grew
    to 19 tabs the tail tabs were unreachable. This is a plain two-row nav
    (group buttons, then the pages of the open group) built on flex-wrap and
    logical properties: nothing is ever hidden behind a scroll, every page is
    at most two clicks away, and the active page is always on screen.
  -->
  <nav class="finance-nav fin-nav" :aria-label="$t('finance.ui.navLabel')">
    <div class="fin-nav-groups">
      <button
        v-for="group in visibleGroups"
        :key="group.key"
        type="button"
        class="fin-nav-group"
        :class="{
          'fin-nav-group--open': openGroup && group.key === openGroup.key,
          'fin-nav-group--current': group.key === activeGroupKey,
        }"
        :aria-expanded="openGroup && group.key === openGroup.key ? 'true' : 'false'"
        :aria-controls="'fin-nav-items-' + group.key"
        @click="onGroupClick(group)"
      >
        <v-icon small class="fin-nav-group-icon">{{ group.icon }}</v-icon>
        <span class="fin-nav-group-label">{{ group.label }}</span>
        <span class="fin-nav-group-count">{{ group.items.length }}</span>
      </button>
    </div>
    <div
      v-if="openGroup"
      :id="'fin-nav-items-' + openGroup.key"
      class="fin-nav-items"
    >
      <router-link
        v-for="item in openGroup.items"
        :key="item.name"
        :to="{ name: item.name }"
        class="fin-nav-item"
        :class="{ 'fin-nav-item--active': isItemActive(item) }"
        :aria-current="isItemActive(item) ? 'page' : null"
      >
        {{ item.label }}
      </router-link>
    </div>
  </nav>
</template>

<script>
export default {
  name: "FinanceSectionNav",
  data() {
    return {
      // Group the user opened by hand; null = follow the current route.
      pickedKey: null,
    };
  },
  computed: {
    groups() {
      // Group membership: final-version doc 2026-10-05 (critic plan A1).
      // Every item keeps the exact permission it carried in the old tab bar.
      return [
        {
          key: "operations",
          icon: "event_note",
          label: this.$t("finance.nav.groups.operations"),
          items: [
            {
              label: this.$t("finance.nav.settlements"),
              name: "payments",
              permission: "VIEW_CAPTAIN_LEDGER",
              // The captain ledger has no item of its own; it is reached
              // from Settlements, so it lights that pill.
              also: ["CaptainLedger"],
            },
            {
              label: this.$t("finance.nav.journalEntries"),
              name: "JournalEntries",
              permission: "MANAGE_FINANCE_JOURNALS",
            },
            {
              label: this.$t("finance.nav.assets"),
              name: "AssetEntries",
              permission: "MANAGE_FINANCE_JOURNALS",
            },
            {
              label: this.$t("finance.nav.adjustments"),
              name: "CaptainAdjustments",
              permission: "MANAGE_FINANCE_ADJUSTMENTS",
            },
            {
              label: this.$t("finance.nav.employees"),
              name: "Employees",
              permission: "VIEW_FINANCE_REPORTS",
            },
          ],
        },
        {
          key: "accountsSetup",
          icon: "settings",
          label: this.$t("finance.nav.groups.accountsSetup"),
          items: [
            {
              label: this.$t("finance.nav.chartOfAccounts"),
              name: "ChartOfAccounts",
              permission: "MANAGE_FINANCE_ACCOUNTS",
            },
            {
              label: this.$t("finance.nav.postingRules"),
              name: "PostingRules",
              permission: "MANAGE_FINANCE_ACCOUNTS",
            },
            {
              // Closing a month is a journal-level act (it refuses postings), so
              // it carries the journals permission rather than the reports one.
              label: this.$t("finance.nav.periodLocks"),
              name: "PeriodLocks",
              permission: "MANAGE_FINANCE_JOURNALS",
            },
          ],
        },
        {
          key: "ledgers",
          icon: "library_books",
          label: this.$t("finance.nav.groups.ledgers"),
          items: [
            {
              label: this.$t("finance.nav.reports"),
              name: "FinanceReports",
              permission: "VIEW_FINANCE_REPORTS",
            },
            // Accountant's document 2026-10-03, section 2: the ledger reports.
            {
              label: this.$t("finance.nav.generalJournalSummary"),
              name: "GeneralJournalSummary",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.accountStatement"),
              name: "AccountStatement",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.driverStatement"),
              name: "DriverStatement",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.accountingBalance"),
              name: "AccountingBalance",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.accountsInventory"),
              name: "AccountsInventory",
              permission: "VIEW_FINANCE_REPORTS",
            },
          ],
        },
        {
          key: "statements",
          icon: "assessment",
          label: this.$t("finance.nav.groups.statements"),
          items: [
            {
              label: this.$t("finance.nav.trialBalance"),
              name: "TrialBalance",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.balanceSheet"),
              name: "BalanceSheet",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.incomeStatement"),
              name: "IncomeStatement",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.cashFlowStatement"),
              name: "CashFlowStatement",
              permission: "VIEW_FINANCE_REPORTS",
            },
            {
              label: this.$t("finance.nav.equityChanges"),
              name: "EquityChanges",
              permission: "VIEW_FINANCE_REPORTS",
            },
          ],
        },
      ];
    },
    // Groups with at least one permitted page; empty groups are dropped.
    visibleGroups() {
      var self = this;
      return this.groups
        .map(function (group) {
          return Object.assign({}, group, {
            items: group.items.filter(function (item) {
              return self.hasPermission(item.permission);
            }),
          });
        })
        .filter(function (group) {
          return group.items.length > 0;
        });
    },
    // The group holding the current page (null on a page outside the nav).
    activeGroupKey() {
      var self = this;
      var found = this.visibleGroups.find(function (group) {
        return group.items.some(function (item) {
          return self.isItemActive(item);
        });
      });
      return found ? found.key : null;
    },
    openGroup() {
      var key = this.pickedKey || this.activeGroupKey;
      return (
        this.visibleGroups.find(function (group) {
          return group.key === key;
        }) ||
        this.visibleGroups[0] ||
        null
      );
    },
  },
  watch: {
    // A new page re-opens the group that holds it.
    "$route.name"() {
      this.pickedKey = null;
    },
  },
  methods: {
    isItemActive(item) {
      var name = this.$route.name;
      return item.name === name || (item.also || []).indexOf(name) !== -1;
    },
    onGroupClick(group) {
      // A group holding a single permitted page is just a link to it.
      if (group.items.length === 1 && !this.isItemActive(group.items[0])) {
        this.$router.push({ name: group.items[0].name });
        return;
      }
      this.pickedKey = group.key;
    },
  },
};
</script>

<!-- Shared compact styles for every finance page (the nav renders on all of them). -->
<style src="@/assets/finance-compact.css"></style>
